import moment from 'moment';
import type { DateInput } from 'interface/common';
import type { DatedRecord, Inclusivity, MomentUnit } from 'interface/date';

import { DEFAULT_DATE_FORMAT, MONTH_MAPPING, WEEKEND_DAYS } from 'constants/date';
import {
  DATE_FORMAT_DEFAULT,
  DATE_FORMAT_FOR_DISPLAY,
  DEFAULT_TIMEZONE,
  FULL_DATE_TIME_FORMAT,
} from 'constants/appConstants';

/**
 * Formats date according to given format.
 *
 * @param date
 * @param format
 * @returns */
export const getFormattedDate = (date?: DateInput, format = DATE_FORMAT_DEFAULT): string => {
  return moment(date || moment()).format(format);
};

/**
 * Checks if the provided date is in the future.
 *
 * @param date
 * @returns */
export function isDateInFuture(date: DateInput): boolean {
  if (!date) return false;
  return moment().diff(date) < 0;
}

/**
 * Sort on the basics of date
 *
 * @param array of objects with a 'startDate' property
 * @param key
 * @returns sorted array on the basis of startDate
 */
export function sortByDate(array: DatedRecord[], key = 'date'): DatedRecord[] {
  return array.sort((a, b) => moment.utc(b[key]).diff(moment.utc(a[key])));
}

/**
 * Sort on the basics of date in ascending order
 *
 * @param array of objects with a 'date' property
 * @param key
 * @returns sorted array on the basis of 'date'
 */
export function sortByAscendingDate(array: DatedRecord[], key = 'date'): DatedRecord[] {
  return array.sort((a, b) => moment.utc(a[key]).diff(moment.utc(b[key])));
}

/**
 * Get the n months ago date from the given date
 *
 * @param current date and number of days to go back
 * @returns date of n months ago
 */
export function getMonthsAgoDate(date: DateInput, noOfMonths = 1): string {
  return getFormattedDate(moment(date).subtract(noOfMonths, 'M'));
}

/**
 * Checks if the date is today or not.
 *
 * @param date
 * @returns :specify valid or not.
 */
export function isDateToday(date: DateInput): boolean {
  return moment(date).isSame(moment(), 'day');
}

/**
 * Return range of dates. Includes both start date and end date.
 *
 * @param startDate
 * @param endDate
 * @param [format=  DEFAULT_DATE_FORMAT]
 * @returns string[]
 */
export function getDateRange(startDate: DateInput, endDate: DateInput, format = DATE_FORMAT_DEFAULT): string[] {
  const start = moment(startDate);
  const end = moment(endDate);

  const dateRange = [];

  while (!start.isAfter(end)) {
    dateRange.push(getFormattedDate(start, format));
    start.add(1, 'day');
  }

  return dateRange;
}

/**
 * Returns current day in number.
 *
 * @returns */
export const getCurrentDay = (): number => moment().day();

/**
 * Calculates the difference between the start date and end date.
 *
 * @param [startDate = null]
 * @param [endDate = null]
 * @returns */

export const getDateDifference = (startDate: DateInput = null, endDate: DateInput = null): number => {
  const startDateMoment = moment(startDate);
  const endDateMoment = moment(endDate);

  return endDateMoment.diff(startDateMoment);
};

/**
 * Get the n months later date from the given date
 *
 * @param * @returns */
export function getMonthsLaterDate(date: DateInput, noOfMonths = 1): string {
  return getFormattedDate(moment(date).add(noOfMonths, 'M'));
}

/**
 * Get the n weeks later date from the given date
 *
 * @param * @returns */
export function getWeeksLaterDate(date: DateInput, noOfWeeks = 1): string {
  return getFormattedDate(moment(date).add(noOfWeeks, 'weeks'));
}

/**
 * Get the start date and end date of the week.
 * Week starts from Monday.
 * Week ends on Sunday.
 *
 * @param date
 * @returns */
export function getCurrentWeekRange(date: DateInput): [string, string] {
  const currentDate = moment(date);

  const weekStart = currentDate.clone().startOf('isoWeek');
  const weekEnd = currentDate.clone().endOf('isoWeek');

  return [getFormattedDate(weekStart), getFormattedDate(weekEnd)];
}

/**
 *
 * Checks whether the selected date falls
 * between start date and end date, inclusive
 * in both sides.
 *
 * @param selectedDate
 * @param startDate
 * @param endDate
 * @param inclusivity
 * @returns */
export const isDateBetweenInclusive = (
  selectedDate: DateInput,
  startDate: DateInput,
  endDate: DateInput,
  inclusivity: Inclusivity = '[]'
): boolean => {
  return moment(selectedDate).isBetween(startDate, endDate, null, inclusivity);
};

/**
 * Get the start date of the month.
 * @param date
 * @returns */
export function getMonthsFirstDate(date: DateInput): string {
  return getFormattedDate(moment(date).startOf('month'));
}

/**
 * Get the end date of the month.
 *
 * @param date
 * @returns */
export function getMonthsLastDate(date: DateInput): string {
  return getFormattedDate(moment(date).endOf('month'));
}

/**
 * Get the number of days from start date to end date.
 *
 * @param startDate
 * @param endDate
 * @returns */
export function getDaysCount(startDate: DateInput, endDate: DateInput): number {
  return moment(endDate).diff(moment(startDate), 'days') + 1;
}

/**
 *
 * Checks if the date is in the past.
 *
 * @param date
 * @param currentDate
 * @param unit
 * @returns */
export function isDateInPast(date: DateInput, currentDate: DateInput = undefined, unit: MomentUnit = 'day'): boolean {
  return moment(date).isBefore(moment(currentDate), unit);
}

/**
 *
 * Checks if the date is same
 *
 * @param date1
 * @param date2
 * @param unit
 * @returns */
export function isSameDate(date1: DateInput, date2: DateInput, unit: MomentUnit = 'day'): boolean {
  return moment(date1).isSame(moment(date2), unit);
}

/**
 * Get the months in a calendar.
 *
 * @returns */
export function getCalendarMonths(): string[] {
  return moment.months();
}

/**
 * Get list of years for calendar.
 *
 * @param pastYearsCount
 * @param futureYearsCount
 * @returns */
export function getCalendarYears(pastYearsCount = 80, futureYearsCount = 2): number[] {
  const currentYear = new Date().getFullYear();

  const years = [];

  for (let i = futureYearsCount; i >= -pastYearsCount; i--) {
    years.push(currentYear + i);
  }

  return years;
}

/**
 * Get the months in a calendar.
 *
 * @returns */
export function getCalendarMonthsOption(): Array<{ label: string; value: number }> {
  const data = moment.months();

  const months = [];

  for (let i = 0; i < data.length; i++) {
    months.push({
      label: MONTH_MAPPING[i],
      value: i,
    });
  }

  return months;
}

/**
 * Get list of years for calendar.
 *
 * @param pastYearsCount
 * @param futureYearsCount
 * @returns */
export function getCalendarYearsOption(
  pastYearsCount = 80,
  futureYearsCount = 2
): Array<{ label: number; value: number }> {
  const currentYear = new Date().getFullYear();

  const years = [];

  for (let i = futureYearsCount; i >= -pastYearsCount; i--) {
    years.push({ label: currentYear + i, value: currentYear + i });
  }

  return years;
}

export function getWeek(): string[] {
  return ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
}

/**
 * Checks if the given time is in the past than other time.
 *
 * @param time
 * @param compareTime
 * @returns */
export const isTimePast = (time: string, compareTime: string): boolean => {
  return moment(time, 'h:mma').isBefore(moment(compareTime, 'h:mma'));
};

/**
 * Get current time in given timezone.
 *
 * @param timezone
 * @param format
 * @param date
 * @returns *
 */
export const getTimeAtTimeZone = (
  timezone = DEFAULT_TIMEZONE,
  format = FULL_DATE_TIME_FORMAT,
  date?: DateInput
): string => {
  return moment(date).utcOffset(timezone).format(format);
};

/**
 * calculates the date before a specified number of days and formats
 * it according to the provided format.
 * @param days
 * @param [format]
 * @returns */
export const getDateBeforeNDays = (days: number, format = DATE_FORMAT_DEFAULT): string => {
  return moment().subtract(days, 'days').format(format);
};

/**
 * Subtracts a specified number of days from a given date and formats the result.
 * @param date - The date to subtract days from.
 * @param days - The number of days to subtract.
 * @param [format=DEFAULT_DATE_FORMAT] - The format of the output date.
 * @param [inclusive=false] - Whether to include the given date in the calculation.
 * @returns - The formatted date string.
 */
export function subtractDays(date: DateInput, days: number, format = DEFAULT_DATE_FORMAT, inclusive = false): string {
  const momentInstance = inclusive ? moment(date) : moment(date).add(1, 'day');

  return momentInstance.subtract(days, 'days').format(format);
}

/**
 * Filters an array of objects based on a date range.
 *
 * @param data - The array of data objects to be filtered.
 * @param key - The key in the data object that contains the date string.
 * @param startDate - The start date of the range.
 * @param endDate - The end date of the range.
 * @returns - The filtered array of data objects.
 */
export function filterDataByDateRange({
  data,
  key,
  startDate,
  endDate,
}: {
  data: DatedRecord[];
  key: string;
  startDate: DateInput;
  endDate: DateInput;
}): DatedRecord[] {
  return data.filter((item) => {
    const itemDate = moment(item[key]);

    return itemDate.isSameOrAfter(startDate) && itemDate.isSameOrBefore(endDate);
  });
}

/**
 * Checks if the given date is a weekend (Saturday or Sunday).
 *
 * @param date - The date to check.
 * @returns - Returns true if the date is a weekend, otherwise false.
 */
export function isWeekend(date: DateInput): boolean {
  const dayOfWeek = moment(date).day();
  return WEEKEND_DAYS.includes(dayOfWeek);
}

/**
 * Gets the first day of a month with an option to subtract months.
 *
 * @param subtractMonths - Number of months to subtract.
 * @returns - First day of the resulting month in YYYY-MM-DD format.
 */
export function getFirstDayOfMonth(subtractMonths = 0): string {
  return moment().subtract(subtractMonths, 'months').startOf('month').format(DATE_FORMAT_DEFAULT);
}

/**
 * Adds or subtracts months from a given date using Moment.js.
 *
 * @param date - The input date. Can be a string, JavaScript Date object, or a Moment.js object.
 * @param months - The number of months to add. Pass a negative value to subtract months.
 * @returns The resulting date formatted as 'YYYY-MM-DD'.
 *
 * @example
 * // Add 2 months to January 15, 2023
 * addMonths('2023-01-15', 2) // '2023-03-15'
 *
 * @example
 * // Subtract 1 month from February 28, 2023
 * addMonths('2023-02-28', -1) // '2023-01-28'
 */
export function addMonths(date: DateInput, months: number): string {
  return moment(date).add(months, 'months').format(DEFAULT_DATE_FORMAT);
}

/**
 * Validates if the end date is outside the allowed range for sub-contract engagement
 *
 * @param endDate
 * @param transitionDate
 * @param currentTransitionDate
 * @returns
 */
export function isSubContractEndDateOutsideRange(
  endDate: moment.Moment | null | undefined,
  transitionDate: moment.Moment | null | undefined,
  currentTransitionDate: moment.Moment | null | undefined
): boolean {
  if (!endDate || !transitionDate) {
    return false;
  }

  return endDate.isBefore(currentTransitionDate) || endDate.isBefore(transitionDate);
}

/**
 * Validates if the start date is outside the allowed range for sub-contract engagement
 *
 * @param startDate
 * @param endDate
 * @param currentTransitionDate
 * @returns
 */
export function isSubContractStartDateOutsideRange(
  startDate: DateInput,
  endDate: DateInput,
  currentTransitionDate: DateInput
): boolean {
  if (!startDate) {
    return false;
  }

  let isOutsideRange = isDateInPast(startDate, currentTransitionDate);

  if (endDate) {
    isOutsideRange = isOutsideRange || isDateInPast(endDate, startDate);
  }

  return isOutsideRange;
}

export const getEmpHistoryDate = (
  startDate: DateInput,
  endDate: DateInput,
  dateFormat = DATE_FORMAT_FOR_DISPLAY
): string => {
  return endDate
    ? [getFormattedDate(startDate, dateFormat), getFormattedDate(endDate, dateFormat)].join(' - ')
    : getFormattedDate(startDate, dateFormat);
};
